/**
 * React Query hooks for the framework HTTP surface.
 *
 * Reads (useCapCounts/useCaps/useCap/useLetters/useLetter) are
 * declarative useQuery wrappers. Writes (createCap, submitCap,
 * approveCap, etc) are useMutation wrappers that invalidate the
 * relevant query keys on success so the UI refreshes without
 * a manual refetch.
 *
 * Lives under framework/ (not shared/) - it's framework-module
 * data + must not be reachable from twin/ or foundation/. The
 * P0.8 module-boundary lint enforces that.
 *
 * Mirrors backend/framework/{letters,caps,entries}_router.py:
 *   GET    /v1/framework/letters
 *   POST   /v1/framework/letters
 *   GET    /v1/framework/letters/{id}
 *   GET    /v1/framework/caps/counts
 *   GET    /v1/framework/caps?position=...
 *   POST   /v1/framework/caps
 *   GET    /v1/framework/caps/{id}
 *   PATCH  /v1/framework/caps/{id}
 *   POST   /v1/framework/caps/{id}/{submit|approve|send-back|withdraw}
 *   GET    /v1/framework/caps/{id}/entries
 *   POST   /v1/framework/caps/{id}/entries
 *   PATCH  /v1/framework/caps/{id}/entries/{eid}
 *   DELETE /v1/framework/caps/{id}/entries/{eid}
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, api, currentAuthToken } from "shared/api/client";
import { workKeys } from "shared/work/useMyWork";
import { capsKeys, lettersKeys } from "shared/api/queryClient";
import type {
  ExtractedLetter,
  Action,
  CAPCreateIn,
  CAPEntryIn,
  CAPEntryOut,
  CAPEntryPatch,
  CAPOut,
  CAPPatchIn,
  CAPSummary,
  CAPTransition,
  CapControl,
  CapControlLinkIn,
  CapCounts,
  CapEntryControl,
  CapFindingLinkIn,
  CapLinkedFinding,
  DraftActions,
  LetterIntakeIn,
  LetterOut,
  ObservationIn,
  ObservationOut,
  LetterSummary,
  Position,
  SuggestControls,
  WorkflowTransitionIn,
} from "shared/api/types";

// ── Letters ─────────────────────────────────────────────────────────

export function useLetters(): UseQueryResult<LetterSummary[], ApiError> {
  return useQuery({
    queryKey: lettersKeys.list(),
    queryFn: () => api.get<LetterSummary[]>("/v1/framework/letters"),
  });
}

export function useLetter(
  letterId: string | undefined,
): UseQueryResult<LetterOut, ApiError> {
  return useQuery({
    queryKey: letterId
      ? lettersKeys.detail(letterId)
      : ["letters", "detail", "_disabled"],
    queryFn: () => api.get<LetterOut>(`/v1/framework/letters/${letterId}`),
    enabled: !!letterId,
  });
}

/**
 * C9 -- observations by hand.
 *
 * A pasted letter arrives as ONE observation covering the whole text,
 * and every CAP action item hangs off an observation, so a letter
 * raising four requirements had its whole response filed against one
 * undifferentiated "the letter". Splitting it is a person's judgement
 * and there was no way to record it.
 */
export function useAddObservation(
  letterId: string,
): UseMutationResult<ObservationOut, ApiError, ObservationIn> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<ObservationOut>(`/v1/framework/letters/${letterId}/observations`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: lettersKeys.all }),
  });
}

export function usePatchObservation(
  letterId: string,
): UseMutationResult<
  ObservationOut,
  ApiError,
  { observationId: string; body: Partial<ObservationIn> }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ observationId, body }) =>
      api.patch<ObservationOut>(
        `/v1/framework/letters/${letterId}/observations/${observationId}`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: lettersKeys.all }),
  });
}

export function useDeleteObservation(
  letterId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (observationId) =>
      api.delete<void>(
        `/v1/framework/letters/${letterId}/observations/${observationId}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: lettersKeys.all }),
  });
}

export function useIntakeLetter(): UseMutationResult<
  LetterOut,
  ApiError,
  LetterIntakeIn
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post<LetterOut>("/v1/framework/letters", body),
    onSuccess: () => {
      // The letter library list now has one more row.
      void qc.invalidateQueries({ queryKey: lettersKeys.all });
    },
  });
}

// ── CAPs - reads ────────────────────────────────────────────────────

/** Per-position queue counts. Always returns all 3 keys. */
export function useCapCounts(
  // Round 9 E9 -- see useAssessmentQueueCounts. The widget that reads
  // this does not render for a seat holding no position.
  applies = true,
): UseQueryResult<CapCounts, ApiError> {
  return useQuery({
    queryKey: capsKeys.counts(),
    queryFn: () => api.get<CapCounts>("/v1/framework/caps/counts"),
    enabled: applies,
    // The position-queue widget polls every 30s via staleTime default.
  });
}

/** Position-filtered CAP queue. Omit position for an admin-style all-CAPs view. */
export function useCaps(position?: Position): UseQueryResult<CAPSummary[], ApiError> {
  const url = position
    ? `/v1/framework/caps?position=${encodeURIComponent(position)}`
    : "/v1/framework/caps";
  return useQuery({
    queryKey: capsKeys.list(position),
    queryFn: () => api.get<CAPSummary[]>(url),
  });
}

/**
 * D4 -- the Framework tile promises "Upload a regulator PDF; Marsad
 * Engine extracts observations", and the endpoint has existed and
 * worked since the letter track shipped. Nothing in the SPA reached
 * it, so the intake page offered only paste-text and the tile
 * described a capability with no surface.
 *
 * multipart, so it does not go through the JSON client.
 */
export function useUploadLetterPdf(): UseMutationResult<
  LetterOut,
  ApiError,
  { file: File; regulator_code: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, regulator_code }) => {
      const body = new FormData();
      body.append("file", file);
      const token = currentAuthToken();
      const res = await fetch(
        `/v1/framework/letters/upload?regulator_code=${encodeURIComponent(
          regulator_code,
        )}`,
        {
          method: "POST",
          body,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      const text = await res.text();
      const parsed: unknown = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const detail =
          typeof parsed === "object" &&
          parsed !== null &&
          "detail" in parsed &&
          typeof (parsed as { detail: unknown }).detail === "string"
            ? (parsed as { detail: string }).detail
            : res.statusText;
        throw new ApiError(res.status, detail);
      }
      return parsed as LetterOut;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: lettersKeys.all });
    },
  });
}

export function useCap(capId: string | undefined): UseQueryResult<CAPOut, ApiError> {
  return useQuery({
    queryKey: capId ? capsKeys.detail(capId) : ["caps", "detail", "_disabled"],
    queryFn: () => api.get<CAPOut>(`/v1/framework/caps/${capId}`),
    enabled: !!capId,
  });
}

/**
 * The CAP's workflow history (C1).
 *
 * Every transition has always been recorded and hash-chained, comment
 * and all, and shown to nobody -- so the reviewer's MANDATORY send-back
 * comment reached neither the preparer who has to act on it nor the
 * approver who signs after it.
 */
export function useCapTransitions(
  capId: string | undefined,
): UseQueryResult<CAPTransition[], ApiError> {
  return useQuery({
    queryKey: capId
      ? [...capsKeys.detail(capId), "transitions"]
      : ["caps", "transitions", "_disabled"],
    queryFn: () => api.get<CAPTransition[]>(`/v1/framework/caps/${capId}/transitions`),
    enabled: !!capId,
  });
}

// ── CAPs - mutations ────────────────────────────────────────────────

/* Round 19 G38 -- a plan raised or a finding linked changes what its
   letter page shows: its plans, the count, the attestation block and the
   letter's thread. The page read "already has 1 plan" 18 s after a
   second, while its own thread read "2 plans". */
function invalidateLetterViews(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: lettersKeys.all });
  void qc.invalidateQueries({ queryKey: ["attestation-links"] });
  void qc.invalidateQueries({ queryKey: ["thread"] });
}

export function useCreateCap(): UseMutationResult<CAPOut, ApiError, CAPCreateIn> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) => api.post<CAPOut>("/v1/framework/caps", body),
    onSuccess: () => {
      // New CAP lands in the preparer's queue - counts + list both shift.
      void qc.invalidateQueries({ queryKey: capsKeys.all });
      invalidateLetterViews(qc);
    },
  });
}

export function usePatchCap(
  capId: string,
): UseMutationResult<CAPOut, ApiError, CAPPatchIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.patch<CAPOut>(`/v1/framework/caps/${capId}`, body),
    onSuccess: (data) => {
      qc.setQueryData(capsKeys.detail(capId), data);
      void qc.invalidateQueries({ queryKey: capsKeys.list() });
    },
  });
}

/**
 * Workflow transition mutation factory. Pass the action ("submit",
 * "approve", "send_back", "withdraw") and the CAP id; returns a
 * mutation that POSTs the body to the matching endpoint and
 * invalidates the affected queue + detail on success.
 *
 * URL note: action="send_back" maps to the URL suffix "send-back"
 * (dash not underscore) - matches the FastAPI route.
 */
const ACTION_URL: Record<Action, string> = {
  submit: "submit",
  approve: "approve",
  send_back: "send-back",
  withdraw: "withdraw",
};

/**
 * B3 -- the action travels with the CALL, not with the hook.
 *
 * This took the action as an argument, so the view bound it to
 * `activeAction` and the click handler set that state and fired the
 * mutation in the same pass. React had not re-rendered, so the
 * mutation still carried the PREVIOUS action -- `null ?? "submit"` on
 * a first click. Pressing "Approve and close" posted to /submit, the
 * server refused it, and the button appeared to do nothing until it
 * was pressed a second time.
 */
export function useTransitionCap(
  capId: string,
): UseMutationResult<CAPOut, ApiError, WorkflowTransitionIn & { action: Action }> {
  const qc = useQueryClient();
  return useMutation({
    /* ROUND 13 E3 -- the view renders this refusal itself, in a red
       panel beside the buttons. Without this marker the global sink
       raised a toast as well, so one refusal arrived twice: "That did
       not save" over the top of the sentence already on screen. */
    meta: { handled: true },
    mutationFn: ({ action, ...body }) =>
      api.post<CAPOut>(`/v1/framework/caps/${capId}/${ACTION_URL[action]}`, body),
    /* ROUND 13 E3 -- a refused write has to tell the page the truth.
       After a 409 the page re-rendered the stale state as current: a
       plan the server had marked approved read "In review", and the
       button that had just been refused was still live -- so the
       obvious next act is to press it again. The control was identical
       performed slowly, so this is staleness and not timing. */
    onError: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.detail(capId) });
      void qc.invalidateQueries({ queryKey: capsKeys.counts() });
      void qc.invalidateQueries({ queryKey: capsKeys.list() });
    },
    onSuccess: (data) => {
      // A transition shifts the CAP between queues, so counts + lists +
      // detail all need refresh. setQueryData on the detail key gives
      // the UI an instant updated view; the list/counts invalidations
      // refire in the background.
      qc.setQueryData(capsKeys.detail(capId), data);
      void qc.invalidateQueries({ queryKey: capsKeys.counts() });
      void qc.invalidateQueries({ queryKey: capsKeys.list() });
      // C3: the item has left (or joined) an inbox, so the inbox is now
      // wrong until a reload. It listed the CAP after it had been
      // approved.
      void qc.invalidateQueries({ queryKey: workKeys.mine });
      // ...and the history has a new step, including the comment the
      // next person needs (C1).
      void qc.invalidateQueries({
        queryKey: [...capsKeys.detail(capId), "transitions"],
      });
    },
  });
}

// ── Entries ─────────────────────────────────────────────────────────

export function useEntries(
  capId: string | undefined,
): UseQueryResult<CAPEntryOut[], ApiError> {
  return useQuery({
    queryKey: capId ? capsKeys.entries(capId) : ["caps", "entries", "_disabled"],
    queryFn: () => api.get<CAPEntryOut[]>(`/v1/framework/caps/${capId}/entries`),
    enabled: !!capId,
  });
}

export function useCreateEntry(
  capId: string,
): UseMutationResult<CAPEntryOut, ApiError, CAPEntryIn> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<CAPEntryOut>(`/v1/framework/caps/${capId}/entries`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.entries(capId) });
      // entry_count on the parent CAP detail also changes.
      void qc.invalidateQueries({ queryKey: capsKeys.detail(capId) });
    },
  });
}

export function usePatchEntry(
  capId: string,
  entryId: string,
): UseMutationResult<CAPEntryOut, ApiError, CAPEntryPatch> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.patch<CAPEntryOut>(`/v1/framework/caps/${capId}/entries/${entryId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.entries(capId) });
      void qc.invalidateQueries({ queryKey: capsKeys.detail(capId) });
    },
  });
}

export function useDeleteEntry(
  capId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId) =>
      api.delete<void>(`/v1/framework/caps/${capId}/entries/${entryId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.entries(capId) });
      void qc.invalidateQueries({ queryKey: capsKeys.detail(capId) });
    },
  });
}

// ── CAP <-> control links (P108.2 -- the CAP leg) ────────────────────

export function useCapControls(
  capId: string | undefined,
): UseQueryResult<CapControl[], ApiError> {
  return useQuery({
    queryKey: capId ? capsKeys.controls(capId) : ["caps", "controls", "_disabled"],
    queryFn: () => api.get<CapControl[]>(`/v1/framework/caps/${capId}/controls`),
    enabled: Boolean(capId),
    // Round 17 A31 -- a CAP opened right after it was raised read an
    // empty list from before its control was linked. Always re-read on
    // arrival; the page says "reading" until the fresh answer lands.
    refetchOnMount: "always",
  });
}

/** P139: Engine ranks which controls this CAP remediates (read-only). */
export function useSuggestControls(
  capId: string,
): UseMutationResult<SuggestControls, ApiError, { framework?: string }> {
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: ({ framework }) =>
      api.post<SuggestControls>(
        `/v1/framework/caps/${capId}/suggest-controls${
          framework ? `?framework=${encodeURIComponent(framework)}` : ""
        }`,
      ),
  });
}

export function useDraftCapActions(
  capId: string,
): UseMutationResult<DraftActions, ApiError, void> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: () =>
      api.post<DraftActions>(`/v1/framework/caps/${capId}/draft-actions`),
    onSuccess: () => {
      // The drafted action items land on the CAP -> refetch the detail.
      void qc.invalidateQueries({ queryKey: capsKeys.detail(capId) });
    },
  });
}

export function useAddCapControl(
  capId: string,
): UseMutationResult<CapControl, ApiError, CapControlLinkIn> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<CapControl>(`/v1/framework/caps/${capId}/controls`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.controls(capId) });
    },
  });
}

export function useRemoveCapControl(
  capId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    // arg = framework_control_id
    mutationFn: (controlId) =>
      api.delete<void>(`/v1/framework/caps/${capId}/controls/${controlId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.controls(capId) });
    },
  });
}

// ── P115.7: entry-grained CAP <-> control links ────────────────────

export function useEntryControls(
  capId: string,
  entryId: string,
): UseQueryResult<CapEntryControl[], ApiError> {
  return useQuery({
    queryKey: capsKeys.entryControls(capId, entryId),
    queryFn: () =>
      api.get<CapEntryControl[]>(
        `/v1/framework/caps/${capId}/entries/${entryId}/controls`,
      ),
    enabled: Boolean(capId && entryId),
  });
}

export function useAddEntryControl(
  capId: string,
  entryId: string,
): UseMutationResult<CapEntryControl, ApiError, CapControlLinkIn> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<CapEntryControl>(
        `/v1/framework/caps/${capId}/entries/${entryId}/controls`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: capsKeys.entryControls(capId, entryId),
      });
    },
  });
}

export function useRemoveEntryControl(
  capId: string,
  entryId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    // arg = framework_control_id
    mutationFn: (controlId) =>
      api.delete<void>(
        `/v1/framework/caps/${capId}/entries/${entryId}/controls/${controlId}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: capsKeys.entryControls(capId, entryId),
      });
    },
  });
}

// ── P76: CAP <-> finding links ─────────────────────────────────────

export function useCapFindings(
  capId: string | undefined,
): UseQueryResult<CapLinkedFinding[], ApiError> {
  return useQuery({
    queryKey: capId ? capsKeys.findings(capId) : ["caps", "findings", "_disabled"],
    queryFn: () => api.get<CapLinkedFinding[]>(`/v1/framework/caps/${capId}/findings`),
    enabled: Boolean(capId),
  });
}

export function useAddCapFinding(
  capId: string,
): UseMutationResult<CapLinkedFinding, ApiError, CapFindingLinkIn> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<CapLinkedFinding>(`/v1/framework/caps/${capId}/findings`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.findings(capId) });
      invalidateLetterViews(qc);
    },
  });
}

export function useRemoveCapFinding(
  capId: string,
): UseMutationResult<void, ApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    // arg = finding_id
    mutationFn: (findingId) =>
      api.delete<void>(`/v1/framework/caps/${capId}/findings/${findingId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.findings(capId) });
      invalidateLetterViews(qc);
    },
  });
}

/**
 * B8 -- read the PDF WITHOUT recording it.
 *
 * Upload went straight to "Letter recorded": the Engine's proposed
 * title and the upload timestamp became the letter's, and the
 * observations -- which every CAP action item later hangs off -- could
 * not be touched. A letter anchors months of remediation, and the
 * person responsible for it had no moment at which to say "that is not
 * what it says".
 */
export function useExtractLetterPdf(): UseMutationResult<
  ExtractedLetter,
  ApiError,
  { file: File }
> {
  return useMutation({
    mutationFn: async ({ file }) => {
      const body = new FormData();
      body.append("file", file);
      const token = currentAuthToken();
      const res = await fetch("/v1/framework/letters/extract", {
        method: "POST",
        body,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new ApiError(
          res.status,
          typeof detail?.detail === "string" ? detail.detail : res.statusText,
        );
      }
      return (await res.json()) as ExtractedLetter;
    },
  });
}

/**
 * D11 -- give a drafted action an owner and a date.
 *
 * The endpoint has always accepted both; there was simply no way to
 * reach it from the CAP page, so an action the Engine drafted stayed
 * unowned and a reviewer's challenge asking who owns this could not be
 * answered.
 *
 * The server applies only the fields it was sent, so omitting one
 * leaves it as it was. It cannot CLEAR a field, which is a real limit
 * and a small one: taking an owner off an action is rare, and it is
 * better than the alternative reading, where a form that sends every
 * field wipes what another person just agreed.
 */
export function usePatchCapEntry(
  capId: string,
  entryId: string,
): UseMutationResult<
  CAPEntryOut,
  ApiError,
  { owner_role?: string | null; due_date?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api.patch<CAPEntryOut>(`/v1/framework/caps/${capId}/entries/${entryId}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: capsKeys.detail(capId) });
    },
  });
}
